import {
  isNone,
  v2_selectMultipleChoiceConfigurationPropertyValue,
  v2_useAddConfigurationToCart,
  v2_useCart,
  v2_useProduct,
} from '@crocodille/common';
import { useAppLayoutState } from '@layouts/app-layout/app-layout';
import { useCallback, useRef } from 'react';
import Addons from './addons';

export interface AddonsModalProps {
  productSlug: string;
  onClose: () => void;
}

function AddonsModal({ productSlug, onClose }: AddonsModalProps) {
  const { data: cart } = v2_useCart();

  const {
    data: product,
    configurationValues,
    filteredConfiguration,
    clearConfiguration,
    setMultipleChoiceProperty,
  } = v2_useProduct(productSlug);

  const {
    addConfigurationToCart,
    isMutating: addingToCart,
    addConfigurationToCartExcludingRoles,
  } = v2_useAddConfigurationToCart({
    product,
    filteredConfiguration,
    configurationValues,
  });

  const { toggleCartOpen } = useAppLayoutState();

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const clearConfigurationRef = useRef(clearConfiguration);
  clearConfigurationRef.current = clearConfiguration;
  const addConfigurationToCartRef = useRef(addConfigurationToCart);
  addConfigurationToCartRef.current = addConfigurationToCart;
  const addConfigurationToCartExcludingRolesRef = useRef(
    addConfigurationToCartExcludingRoles,
  );
  addConfigurationToCartExcludingRolesRef.current =
    addConfigurationToCartExcludingRoles;

  const handleConfirm = useCallback(async () => {
    const added = await addConfigurationToCartRef.current();
    if (added) {
      clearConfigurationRef.current();
      toggleCartOpen(true);
      onCloseRef.current();
    }
  }, [toggleCartOpen]);

  const handleDecline = useCallback(async () => {
    try {
      const added = await addConfigurationToCartExcludingRolesRef.current([
        'addon',
      ]);
      if (added) {
        clearConfigurationRef.current();
        onCloseRef.current();
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const addonProperty = filteredConfiguration.find((p) => p.role === 'addon');

  if (isNone(product) || !addonProperty) {
    return null;
  }

  const addonValues = v2_selectMultipleChoiceConfigurationPropertyValue(
    configurationValues,
    addonProperty.id,
  );

  return (
    <Addons
      open
      options={addonProperty.options}
      values={addonValues}
      showPrices={cart != null}
      onToggle={(optionId, qty) =>
        setMultipleChoiceProperty(addonProperty.id, optionId, qty)
      }
      onConfirm={handleConfirm}
      onDecline={handleDecline}
      addingToCart={addingToCart}
    />
  );
}

export default AddonsModal;
